import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Archive, ArchiveRestore, ChevronDown, ChevronRight } from 'lucide-react'
import { useCollaborations } from '@/hooks/useCollaborations'
import { toast } from '@/components/ui/sonner'
import { useSetCollaborationArchivedMutation } from '@/redux/collaborationsApi'
import { cn } from '@/lib/utils'
import {
  DEAL_TYPE_LABELS,
  STAGES,
  dealTypeOf,
  stageOf,
  dealStatusLine,
  type DealLike,
} from '@/lib/dealStages'
import type { Collaboration } from '@/redux/collaborationsApi'

const money = (n?: number | null) =>
  n && n > 0 ? `$${Math.round(n).toLocaleString()}` : 'Product'

/** Moves a deal to or from Archived. Only the athlete's own lists change. */
const ArchiveButton: React.FC<{ deal: Collaboration }> = ({ deal }) => {
  const [setArchived, { isLoading }] = useSetCollaborationArchivedMutation()
  const archived = !!deal.archived
  return (
    <button
      type="button"
      disabled={isLoading}
      onClick={async () => {
        try {
          await setArchived({ id: deal.id, archived: !archived }).unwrap()
          toast(
            archived ? 'Moved back to your deals' : 'Archived. Find it at the bottom of Deals.',
          )
        } catch {
          toast('Could not update the deal. Try again.')
        }
      }}
      aria-label={archived ? `Unarchive ${deal.title}` : `Archive ${deal.title}`}
      title={archived ? 'Unarchive' : 'Archive'}
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-gray-600 hover:bg-foreground/10 hover:text-foreground disabled:opacity-50"
    >
      {archived ? <ArchiveRestore className="h-5 w-5" /> : <Archive className="h-5 w-5" />}
    </button>
  )
}

const DealRow: React.FC<{ deal: Collaboration }> = ({ deal }) => {
  const navigate = useNavigate()
  const s = stageOf(deal as DealLike)
  const mine = s.waitingOn === 'athlete' && !deal.archived
  return (
    <li
      className={cn(
        'flex items-center gap-1 rounded-2xl border pr-1.5 transition',
        mine
          ? 'border-[#C0FD72]/60 bg-[#C0FD72]/10'
          : 'border-foreground/10 bg-card hover:bg-foreground/10 shadow-[0_1px_2px_rgba(30,41,59,0.04)]',
        (s.ended || deal.archived) && 'opacity-60',
      )}
    >
      <button
        type="button"
        onClick={() => navigate(`/deals/${deal.id}`)}
        className="flex min-w-0 flex-1 items-center gap-3 p-3.5 pr-1 text-left"
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-muted text-sm font-bold text-foreground">
          {deal.brand?.logo ? (
            <img src={deal.brand.logo} alt="" className="h-full w-full object-cover" />
          ) : (
            (deal.brand?.name ?? '?').slice(0, 2).toUpperCase()
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-foreground">
              {deal.brand?.name ?? 'Brand'} · {deal.title}
            </span>
          </span>
          <span className="mt-0.5 block truncate text-xs text-gray-600">
            {DEAL_TYPE_LABELS[dealTypeOf(deal as DealLike)]} · {money(deal.paymentAmount)} ·{' '}
            {STAGES[s.index].label}
          </span>
          <span
            className={cn(
              'mt-1 block text-xs',
              mine ? 'font-semibold text-[#4d7c0f]' : 'text-gray-500',
            )}
          >
            {dealStatusLine(deal as DealLike, s)}
          </span>
        </span>
        <ChevronRight className="h-5 w-5 shrink-0 text-gray-500" />
      </button>
      <ArchiveButton deal={deal} />
    </li>
  )
}

/**
 * Every deal in one list, the ones that need the athlete first. Replaces the tabbed
 * Collaborations page. Deals the athlete archived collect in a collapsed section at the end.
 */
const Deals: React.FC = () => {
  const navigate = useNavigate()
  const { collaborations, isLoading } = useCollaborations()
  const [showArchived, setShowArchived] = useState(false)

  const groups = {
    yours: [] as Collaboration[],
    active: [] as Collaboration[],
    done: [] as Collaboration[],
    archived: [] as Collaboration[],
  }
  for (const d of collaborations) {
    const s = stageOf(d as DealLike)
    // Archived deals wait at the bottom, out of the way, until the athlete brings them back
    if (d.archived) groups.archived.push(d)
    else if (s.ended || s.stage === 'paid') groups.done.push(d)
    else if (s.waitingOn === 'athlete') groups.yours.push(d)
    else groups.active.push(d)
  }

  const Section: React.FC<{ title: string; items: Collaboration[]; empty?: string }> = ({
    title,
    items,
    empty,
  }) =>
    items.length || empty ? (
      <section>
        <h2 className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-600">
          {title} {items.length ? `· ${items.length}` : ''}
        </h2>
        {items.length ? (
          <ul className="space-y-2">
            {items.map((d) => (
              <DealRow key={d.id} deal={d} />
            ))}
          </ul>
        ) : (
          <p className="rounded-2xl border border-dashed border-foreground/15 p-4 text-sm text-gray-500">
            {empty}
          </p>
        )}
      </section>
    ) : null

  return (
    <div className="mx-auto w-full max-w-md pb-8">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-foreground">Deals</h1>
        <p className="text-sm text-gray-600">Offers, work in progress, and what you've earned.</p>
      </div>

      {isLoading && collaborations.length === 0 ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="space-y-6">
          <Section
            title="Your move"
            items={groups.yours}
            empty="Nothing waiting on you. Browse opportunities to find your next deal."
          />
          <Section title="In progress" items={groups.active} />
          <Section title="Done" items={groups.done} />
          {groups.archived.length ? (
            <section>
              <button
                type="button"
                onClick={() => setShowArchived((v) => !v)}
                aria-expanded={showArchived}
                className="flex min-h-11 w-full items-center justify-between text-xs font-bold uppercase tracking-wider text-gray-600"
              >
                Archived · {groups.archived.length}
                <ChevronDown className={cn('h-4 w-4 transition', showArchived && 'rotate-180')} />
              </button>
              {showArchived ? (
                <ul className="mt-2 space-y-2">
                  {groups.archived.map((d) => (
                    <DealRow key={d.id} deal={d} />
                  ))}
                </ul>
              ) : null}
            </section>
          ) : null}
          <button
            type="button"
            onClick={() => navigate('/opportunities/all')}
            className="w-full rounded-2xl border border-foreground/15 py-3 text-sm font-semibold text-foreground hover:bg-foreground/10"
          >
            Find opportunities
          </button>
        </div>
      )}
    </div>
  )
}

export default Deals
